import { useRouter } from 'next/router';
import Head from 'next/head';
import { IProductObject } from '@features/categories/typescript';
import { PUBLIC_HOST } from '@config/index';
import getProductUrl from '@utils/getProductUrl';
import getOffer from '@utils/getOffer';
import { getRegion } from '@utils/misc';
import { getCurrencyByRegion, toISO4217CurrencyCode } from '@utils/formatPrice';
import ProductState from '@features/categories/mobx/state/ProductState';
type PropsType = {
  products: IProductObject[];
  currentPage?: number;
  totalItems?: number;
};
const parsePriceForSchema = (priceString: string, currency: string): number | null => {
  if (!priceString) return null;
  const removeCurrencySymbol = currency === 'MXN' ? [2] : [0, -2];
  const numericPart = priceString.slice(...(removeCurrencySymbol as [number, number?])).replace(/\s/g, '').replace(',', '.');
  const price = Number(numericPart);
  return Number.isNaN(price) ? null : price;
};
const ItemListSchema = ({
  products,
  currentPage = 1,
  totalItems
}: PropsType) => {
  const router = useRouter();
  const {
    locale,
    defaultLocale
  } = router;
  const [_, priceListName] = getOffer();
  const currency = toISO4217CurrencyCode(getCurrencyByRegion(getRegion()));
  if (!products || products.length === 0) {
    return null;
  }
  const getFullProductUrl = (product: IProductObject): string => {
    const productUrl = getProductUrl(product.name, product.id);
    const localePrefix = locale && locale !== defaultLocale ? `/${locale}` : '';
    return `${PUBLIC_HOST}${localePrefix}${productUrl}`;
  };
  const getCurrentPageUrl = (): string => {
    const {
      asPath
    } = router;
    const pathWithoutQuery = asPath.split('?')[0];
    const localePrefix = locale && locale !== defaultLocale ? `/${locale}` : '';
    return `${PUBLIC_HOST}${localePrefix}${pathWithoutQuery}${currentPage > 1 ? `?page=${currentPage}` : ''}`;
  };
  const getPageUrl = (page: number): string => {
    const {
      asPath
    } = router;
    const pathWithoutQuery = asPath.split('?')[0];
    const localePrefix = locale && locale !== defaultLocale ? `/${locale}` : '';
    return `${PUBLIC_HOST}${localePrefix}${pathWithoutQuery}${page > 1 ? `?page=${page}` : ''}`;
  };
  const getProductImage = (product: IProductObject): string | undefined => {
    const mainImage = product.packshots?.main?.[0];
    if (mainImage?.url) {
      return mainImage.url.startsWith('http') ? mainImage.url : `${PUBLIC_HOST}${mainImage.url}`;
    }
    return undefined;
  };
  const pageSize = products.length;
  const totalPages = totalItems && pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;
  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: products.map((product, index) => {
      const productState = new ProductState(product);
      const productUrl = getFullProductUrl(productState);
      const price = parsePriceForSchema(productState?.getPrice(priceListName), currency) ?? parsePriceForSchema(productState?.getPromoPrice(priceListName), currency);
      return {
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: product.name,
          url: productUrl,
          ...(getProductImage(productState) && {
            image: getProductImage(productState)
          }),
          ...(productState?.manufacturer && {
            brand: {
              '@type': 'Brand',
              name: productState?.manufacturer.name
            }
          }),
          ...(price !== null && {
            offers: {
              '@type': 'Offer',
              price,
              priceCurrency: currency,
              url: productUrl,
              availability: 'https://schema.org/InStock',
              condition: 'https://schema.org/NewCondition'
            }
          })
        }
      };
    }),
    ...(totalItems && {
      numberOfItems: totalItems
    }),
    url: getCurrentPageUrl(),
    ...(hasNextPage && {
      nextItem: {
        '@type': 'ItemList',
        url: getPageUrl(currentPage + 1)
      }
    }),
    ...(hasPreviousPage && {
      previousItem: {
        '@type': 'ItemList',
        url: getPageUrl(currentPage - 1)
      }
    })
  };
  return <Head>
      <script id="item-list-schema" type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(itemListSchema)
    }} />
    </Head>;
};
export default ItemListSchema;